import amplitude from 'amplitude-js';
import config from '../../config';
import { capitalize, get, pick } from 'lodash';
import { all, call, fork, put, takeEvery } from 'redux-saga/effects';
import { APP_INITIALIZED } from '../constants/app';
import {
    INVITE_USER_SUCCESS,
    CANCEL_INVITATION_SUCCESS,
    RESEND_INVITATION_SUCCESS,
    SEND_REQUEST_ACCESS,
    REQUEST_ACCESS_OPEN_PAGE,
    SIGNUP_USER_SUCCESS_OPEN_PAGE
} from '../users';
import {
    USER_LOGGED_IN,
    USER_INVITE,
    USER_INVITE_RESEND,
    USER_INVITE_CANCEL
} from '../constants/user';
import {
    SUBMISSION_CLEARANCE_PASSED,
    SUBMISSION_CLEARANCE_FAILED,
    SUBMISSION_CLEARANCE_PENDING,
    SUBMISSION_KNOCKED_OUT,
    SUBMISSION_FAILED,
    SUBMISSION_CREATE,
    SUBMISSION_EDIT,
    SUBMISSION_CREATE_SUCCESS,
    SUBMISSION_EDIT_SUCCESS,
    CHANGE_SUBMISSION,
    CHANGE_SUBMISSION_COVERAGE
} from '../constants/submission';

import {
    SIGNUP_NEW_PASSWORD_INITIALIZED,
    SIGNUP_NEW_PASSWORD_SAVED,
    SIGNUP_PROFILE_INITIALIZED,
    SIGNUP_PROFILE_SAVED
} from '../constants/signup';

import Helpers from './helpers';

amplitude.getInstance().init(config.amplitudeAppToken);

const events = {
    auth: {
        login: 'Authentication: Login',
        logout: 'Authentication: Logout',
        resetPassword: 'Authentication: Reset Password',
        requestAccess: 'Request Access',
        openRequestAccessOpenPage: 'Sign-up Outcome',
        signUpSuccessOpenPage: 'Sign-up Outcome',
    },
    onboarding: {
        initialized: 'Onboarding: New Password Opened',
        newPasswordSet: 'Onboarding: New Password Created',
        profileInitialized: 'Onboarding: Profile Opened',
        profileCompleted: 'Onboarding: Profile Completed'
    },
    referral: {
        create: 'Referral: Invite',
        accept: 'Referral: Invite Accept',
        resend: 'Referral: Invite Resend',
        cancel: 'Referral: Invite Cancel'
    },
    submission: {
        changeCoverageOption: 'Submission: Change Coverage Option',
        clearance: 'Submission: Clearance',
        edit: 'Submission: Edit',
        new: 'Submission: New',
        submit: 'Submission: Submit'
    }
};

const configureAmplitude = ({ brokerName, username, email, role, brokerId, id }) => {
    const userProperties = {
        User: {
            Email: email,
            BrokerageName: brokerName,
            BrokerId: brokerId,
            Role: role.includes('admin') ? 'Admin' : 'Regular'
        },
        Session: {
            Environment: config.nodeEnv,
        }
    };
    amplitude.getInstance().setUserProperties(userProperties);
};

function* inviteUserSuccess(action) {
    const { brokerageId, email, isAdmin, userType, currentUser } = action.value;
    const eventData = {
        BrokerageId: brokerageId,
        Email: email,
        Role: `${userType}:${isAdmin ? 'admin' : 'regular'}`,
        ReferrerUserEmail: currentUser.email,
        ReferrerUserType: currentUser.role
    };
    amplitude.getInstance().logEvent(events.referral.create, eventData);
}

function* cancelInvitationSuccess(action) {
    const { user, currentUser } = action.value;
    const { brokerId, email } = user;
    const eventData = {
        BrokerageId: brokerId,
        Email: email,
        CancelledBy: currentUser.email,
        CancelledByUserType: currentUser.role
    };
    amplitude.getInstance().logEvent(events.referral.cancel, eventData);
}

function* resendInvitationSuccess(action) {
    const { user, currentUser } = action.value;
    const { brokerId, email, isAdmin, userType } = user;
    const eventData = {
        BrokerageId: brokerId,
        Email: email,
        ResendBy: currentUser.email,
        ResendByUserType: currentUser.role
    };
    amplitude.getInstance().logEvent(events.referral.resend, eventData);
}

function* watchForInviteUserSuccess() {
    yield takeEvery(INVITE_USER_SUCCESS, inviteUserSuccess);
}

function* watchForCancelInvitationSuccess() {
    yield takeEvery(CANCEL_INVITATION_SUCCESS, cancelInvitationSuccess);
}

function* watchForResendInvitationSuccess() {
    yield takeEvery(RESEND_INVITATION_SUCCESS, resendInvitationSuccess);
}

function* watchForUserInvite() {
    yield takeEvery(USER_INVITE, action => {
        const { email, role, referrerUserType, eventSource } = action.value;
        const eventData = {
            Email: email,
            Role: role.includes('admin') ? 'Admin' : 'Regular',
            ReferrerUserType: referrerUserType,
            Page: eventSource
        };
        amplitude.getInstance().logEvent(events.referral.create, eventData);
    });
}

function* watchForUserInviteResend() {
    yield takeEvery(USER_INVITE_RESEND, action => {
        const eventData = { Email: action.value };
        amplitude.getInstance().logEvent(events.referral.resend, eventData);
    });
}

function* watchForUserInviteCancel() {
    yield takeEvery(USER_INVITE_CANCEL, action => {
        const { user, eventSource } = action.value;
        const { email, role } = user;
        const eventData = {
            Email: email,
            Role: role.includes('admin') ? 'Admin' : 'Regular',
            Location: eventSource
        };
        amplitude.getInstance().logEvent(events.referral.cancel, eventData);
    });
}

function* watchForSubmissionCreateSuccess() {
    yield takeEvery(SUBMISSION_CREATE_SUCCESS, action => {
        const submission = action.value.submission || action.value;
        const payload = action.value.payload;

        const eventData = Helpers.transformSubmissionToEventData(submission, 'New', payload);
        amplitude.getInstance().logEvent(events.submission.submit, eventData);

        const revenueEventProperties = pick(eventData, [
            'ClearanceStatus',
            'SubmissionId',
            'SubmissionStatus',
            'NamedInsured',
            'ProjectAddressState'
        ]);

        const revenue = new amplitude.Revenue()
            .setProductId(eventData.Product)
            .setPrice(eventData.PremiumGLTotal)
            .setQuantity(1)
            .setRevenueType('QUOTE')
            .setEventProperties(revenueEventProperties);
        amplitude.getInstance().logRevenueV2(revenue);

        if (eventData.PremiumExcessTotal && eventData.PremiumExcessTotal > 0) {
            const revenue = new amplitude.Revenue()
                .setProductId(`${eventData.Product}-EXCESS`)
                .setPrice(eventData.PremiumExcessTotal)
                .setQuantity(1)
                .setRevenueType('QUOTE')
                .setEventProperties(revenueEventProperties);
            amplitude.getInstance().logRevenueV2(revenue);
        }
    });
}

function* watchForSubmissionEditSuccess() {
    yield takeEvery(SUBMISSION_EDIT_SUCCESS, action => {
        const { submission, quoteCount } = action.value;
        const payload = action.value.payload;

        const eventData = Helpers.transformSubmissionToEventData(
            submission,
            quoteCount ? 'Change Coverage' : 'Edit',
            payload
        );
        amplitude.getInstance().logEvent(events.submission.submit, eventData);

        const revenueEventProperties = pick(eventData, [
            'ClearanceStatus',
            'SubmissionId',
            'SubmissionStatus',
            'NamedInsured',
            'ProjectAddressState'
        ]);

        const revenue = new amplitude.Revenue()
            .setProductId(eventData.Product)
            .setPrice(eventData.PremiumGLTotal)
            .setQuantity(1)
            .setRevenueType('QUOTE')
            .setEventProperties(revenueEventProperties);
        amplitude.getInstance().logRevenueV2(revenue);
    });
}

function* watchForSubmissionClearancePassed() {
    yield takeEvery(SUBMISSION_CLEARANCE_PASSED, action => {
        const { submissionType } = action.value;
        const eventData = {
            Product: submissionType ? submissionType.toUpperCase() : '',
            ClearanceStatus: 'Pass'
        };
        amplitude.getInstance().logEvent(events.submission.clearance, eventData);
    });
}

function* watchForSubmissionClearanceFailed() {
    yield takeEvery(SUBMISSION_CLEARANCE_FAILED, action => {
        const { submissionType, matches } = action.value;
        const eventData = {
            Product: submissionType ? submissionType.toUpperCase() : '',
            ClearanceStatus: 'Fail',
            Matches: matches
        };
        amplitude.getInstance().logEvent(events.submission.clearance, eventData);
    });
}

function* watchForSubmissionClearancePending() {
    yield takeEvery(SUBMISSION_CLEARANCE_PENDING, action => {
        const { submissionType, matches } = action.value;
        const eventData = {
            Product: submissionType ? submissionType.toUpperCase() : '',
            ClearanceStatus: 'Pending',
            Matches: matches
        };
        amplitude.getInstance().logEvent(events.submission.clearance, eventData);
    });
}

function* watchForUserLogin() {
    yield takeEvery(USER_LOGGED_IN, action => {
        configureAmplitude(action.payload);
        const eventData = { Email: action.payload.email };
        amplitude.getInstance().logEvent(events.auth.login, eventData);
    });
}

function* watchForNewPasswordInitialized() {
    yield takeEvery(SIGNUP_NEW_PASSWORD_INITIALIZED, action => {
        const email = action.value;
        const eventData = { Email: action.value };
        amplitude.getInstance().logEvent(events.onboarding.initialized, eventData);
    });
}

function* watchForNewPasswordSaved() {
    yield takeEvery(SIGNUP_NEW_PASSWORD_SAVED, action => {
        const email = action.value;
        const eventData = { Email: action.value };
        amplitude.getInstance().logEvent(events.onboarding.newPasswordSet, eventData);
    });
}

function* watchForProfileInitialized() {
    yield takeEvery(SIGNUP_PROFILE_INITIALIZED, action => {
        const email = action.value;
        const eventData = { Email: action.value };
        amplitude.getInstance().logEvent(events.onboarding.profileInitialized, eventData);
    });
}

function* watchForProfileSaved() {
    yield takeEvery(SIGNUP_PROFILE_SAVED, action => {
        const email = action.value;
        const eventData = { Email: action.value };
        amplitude.getInstance().logEvent(events.onboarding.profileCompleted, eventData);
    });
}

function* watchForChangeSubmission() {
    yield takeEvery(CHANGE_SUBMISSION, action => { });
}

function* watchForChangeSubmissionCoverageOption() {
    yield takeEvery(CHANGE_SUBMISSION_COVERAGE, action => {
        const { submission, selectionType, selectionAction, selectedValue } = action.value;
        const { clearanceStatus, pricingSatus, type, id } = submission; // known typo in the object model
        const eventData = {
            clearanceStatus,
            outcome: pricingSatus,
            product: type,
            submissionId: id,
            selectionValue: selectedValue,
            selectionType,
            action: selectionAction
        };
        amplitude.getInstance().logEvent(events.submission.changeCoverageOption, eventData);
    });
}

function* watchForSubmissionCreate() {
    yield takeEvery(SUBMISSION_CREATE, action => {
        const eventData = ['Type', 'ClearanceStatus'].reduce((acc, cur) => {
            const upperCase = {};
            if (acc[cur]) {
                upperCase[cur] = acc[cur].toUpperCase();
            }
            return Object.assign(acc, upperCase);
        }, action.value);
        amplitude.getInstance().logEvent(events.submission.new, eventData);
    });
}

function* watchForSubmissionEdit() {
    yield takeEvery(SUBMISSION_EDIT, action => {
        const submission = action.value;
        const rating = submission.ratings[submission.type];
        const {
            additionalCoverage,
            excessPremium,
            excessTerrorPremium,
            instantQuote,
            premium,
            terrorPremium,
            totalExcessPremium,
            totalPremium
        } = rating;
        const eventData = {
            PriorOutcome: instantQuote ? 'Quoted' : 'Referral',
            PriorPremiumGLAdditionalCoverage: additionalCoverage,
            PriorPremiumGLBase: premium,
            PriorPremiumGLTerror: terrorPremium,
            PriorPremiumGLTotal: totalPremium,
            PriorPremiumExcessBase: excessPremium,
            PriorPremiumExcessTerror: excessTerrorPremium,
            PriorPremiumExcessTotal: totalExcessPremium,
            ClearanceStatus: submission.clearanceStatus
                ? capitalize(submission.clearanceStatus)
                : null,
            Product: submission.type ? submission.type.toUpperCase() : null,
            SubmissionId: submission.id,
            SubmissionStatus: submission.status,
            ReferralReasons: rating.reason ? rating.reason : [],
            NamedInsured: submission.primaryInsuredName,
            ProjectAddressState: submission.projectAddress.projectState
        };
        amplitude.getInstance().logEvent(events.submission.edit, eventData);
    });
}

export function* watchForSendRequestAccess() {
    yield takeEvery(SEND_REQUEST_ACCESS, action => {
        const eventData = { Email: action.value.email };
        amplitude.getInstance().logEvent(events.auth.requestAccess, eventData);
    });
}

export function* watchForRequestAccessOpenPage() {
    yield takeEvery(REQUEST_ACCESS_OPEN_PAGE, action => {
        const eventData = { Email: action.value.email, Outcome: 'Request Access' };
        amplitude.getInstance().logEvent(events.auth.openRequestAccessOpenPage, eventData);
    });
}

export function* watchForSignupSuccessOpenPage() {
    yield takeEvery(SIGNUP_USER_SUCCESS_OPEN_PAGE, action => {
        const eventData = { Email: action.value.email, Outcome: 'Signup' };
        amplitude.getInstance().logEvent(events.auth.signUpSuccessOpenPage, eventData);
    });
}

export default function* root() {
    yield all([
        fork(watchForUserLogin),
        fork(watchForSubmissionCreate),
        fork(watchForSubmissionEdit),
        fork(watchForSubmissionCreateSuccess),
        fork(watchForSubmissionEditSuccess),
        fork(watchForSubmissionClearancePassed),
        fork(watchForSubmissionClearanceFailed),
        fork(watchForSubmissionClearancePending),
        fork(watchForNewPasswordInitialized),
        fork(watchForNewPasswordSaved),
        fork(watchForProfileInitialized),
        fork(watchForProfileSaved),
        fork(watchForUserInvite),
        fork(watchForUserInviteResend),
        fork(watchForUserInviteCancel),
        fork(watchForChangeSubmission),
        fork(watchForInviteUserSuccess),
        fork(watchForCancelInvitationSuccess),
        fork(watchForResendInvitationSuccess),
        fork(watchForChangeSubmissionCoverageOption),
        fork(watchForSendRequestAccess),
        fork(watchForRequestAccessOpenPage),
        fork(watchForSignupSuccessOpenPage)
    ]);
}
